/**
 * @fileoverview Performance Heatmap
 * @description Grid heatmap showing avg response time by hour-of-day × day-of-week.
 *              Helps identify peak load times and performance patterns.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Skeleton,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { appInsightsApi } from '../services/appInsightsApi';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const HOURS = Array.from({ length: 24 }, (_, i) => i);

const TIME_OPTIONS = [
  { key: 'P7D', label: '7d' },
  { key: 'P30D', label: '30d' },
];

// Color scale: green (fast) → yellow → red (slow)
const getHeatColor = (value, max) => {
  if (value == null || max === 0) return '#f5f5f5';
  const ratio = Math.min(value / max, 1);
  if (ratio < 0.25) return '#e8f5e9';
  if (ratio < 0.5) return '#fff9c4';
  if (ratio < 0.75) return '#ffe0b2';
  return '#ffcdd2';
};

const getBorderColor = (value, max) => {
  if (value == null || max === 0) return '#e0e0e0';
  const ratio = Math.min(value / max, 1);
  if (ratio < 0.25) return '#a5d6a7';
  if (ratio < 0.5) return '#fff176';
  if (ratio < 0.75) return '#ffb74d';
  return '#e57373';
};

const PerformanceHeatmap = () => {
  const [timeRange, setTimeRange] = useState('P7D');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [metric, setMetric] = useState('avg_duration'); // avg_duration | request_count | error_rate

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await appInsightsApi.getPerformanceHeatmap({ timespan: timeRange });
      setData(resp?.data || resp);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [timeRange]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <Skeleton variant="rounded" height={320} sx={{ mb: 3 }} />;

  const rows = data?.rows || [];
  if (!rows.length) {
    return (
      <Paper sx={{ p: 3, mb: 3, textAlign: 'center' }}>
        <Typography color="text.secondary">No performance data available.</Typography>
      </Paper>
    );
  }

  // Build grid[day][hour] = value
  const grid = {};
  let maxVal = 0;
  rows.forEach((r) => {
    const day = Math.round(r.day_of_week ?? 0);
    const hour = r.hour_of_day ?? 0;
    const val = r[metric] ?? 0;
    if (!grid[day]) grid[day] = {};
    grid[day][hour] = r;
    if (val > maxVal) maxVal = val;
  });

  const metricLabel = metric === 'avg_duration' ? 'Avg Response (ms)' :
    metric === 'request_count' ? 'Request Count' : 'Error Rate (%)';

  return (
    <Paper sx={{ p: 2, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#01426A' }}>
          Performance Heatmap
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <ToggleButtonGroup
            value={metric}
            exclusive
            onChange={(_, v) => v && setMetric(v)}
            size="small"
          >
            <ToggleButton value="avg_duration" sx={{ textTransform: 'none', fontSize: '0.75rem' }}>Response Time</ToggleButton>
            <ToggleButton value="request_count" sx={{ textTransform: 'none', fontSize: '0.75rem' }}>Volume</ToggleButton>
            <ToggleButton value="error_rate" sx={{ textTransform: 'none', fontSize: '0.75rem' }}>Error Rate</ToggleButton>
          </ToggleButtonGroup>
          <ToggleButtonGroup
            value={timeRange}
            exclusive
            onChange={(_, v) => v && setTimeRange(v)}
            size="small"
          >
            {TIME_OPTIONS.map((t) => (
              <ToggleButton key={t.key} value={t.key} sx={{ textTransform: 'none' }}>{t.label}</ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
      </Box>

      {/* Hour labels */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '50px repeat(24, 1fr)', gap: '2px', fontSize: '0.65rem' }}>
        <Box />
        {HOURS.map((h) => (
          <Box key={h} sx={{ textAlign: 'center', color: 'text.secondary', fontWeight: 500 }}>
            {h.toString().padStart(2, '0')}
          </Box>
        ))}

        {/* Grid rows */}
        {DAYS.map((dayName, dayIdx) => (
          <React.Fragment key={dayIdx}>
            <Box sx={{ display: 'flex', alignItems: 'center', fontWeight: 600, fontSize: '0.7rem', color: 'text.secondary' }}>
              {dayName}
            </Box>
            {HOURS.map((hour) => {
              const cell = grid[dayIdx]?.[hour];
              const val = cell?.[metric] ?? null;
              const displayVal = val != null ? (metric === 'avg_duration' ? `${Math.round(val)}ms` : metric === 'error_rate' ? `${val}%` : val.toLocaleString()) : '—';
              return (
                <Tooltip
                  key={hour}
                  title={
                    cell ? `${dayName} ${hour}:00 — ${metricLabel}: ${displayVal} | Requests: ${cell.request_count || 0} | Errors: ${cell.error_count || 0}` : 'No data'
                  }
                  arrow
                >
                  <Box
                    sx={{
                      height: 28,
                      borderRadius: '3px',
                      bgcolor: getHeatColor(val, maxVal),
                      border: `1px solid ${getBorderColor(val, maxVal)}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.55rem',
                      color: 'text.secondary',
                      cursor: 'default',
                      transition: 'transform 0.1s',
                      '&:hover': { transform: 'scale(1.15)', zIndex: 1 },
                    }}
                  >
                    {val != null && metric === 'avg_duration' ? Math.round(val) : ''}
                  </Box>
                </Tooltip>
              );
            })}
          </React.Fragment>
        ))}
      </Box>

      {/* Legend */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5, justifyContent: 'center' }}>
        <Typography variant="caption" color="text.secondary">Fast</Typography>
        {['#e8f5e9', '#fff9c4', '#ffe0b2', '#ffcdd2'].map((c) => (
          <Box key={c} sx={{ width: 20, height: 12, bgcolor: c, borderRadius: '2px', border: '1px solid #ccc' }} />
        ))}
        <Typography variant="caption" color="text.secondary">Slow</Typography>
      </Box>
    </Paper>
  );
};

export default PerformanceHeatmap;
